import { motion } from 'framer-motion';
import { ChevronUp, ChevronDown, Plus, Eye, Edit } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from './ui/button';
import type { AdminActivity, ActivityFilters } from '../types/activity';

interface ActivitiesTableProps {
  activities: AdminActivity[];
  isLoading: boolean;
  sortBy: ActivityFilters['sortBy'];
  sortOrder: ActivityFilters['sortOrder'];
  onSort: (column: ActivityFilters['sortBy']) => void;
  isEmpty?: boolean;
  onCreateFirst?: () => void;
}

export function ActivitiesTable({ 
  activities, 
  isLoading, 
  sortBy,
  sortOrder,
  onSort,
  isEmpty = false,
  onCreateFirst,
}: ActivitiesTableProps) {
  const navigate = useNavigate();

  const handleActivityClick = (id: string) => {
    navigate(`/admin/activities/${id}`);
  };

  if (isLoading) {
    return (
      <div className="admin-card p-6 space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-admin-dark-700 rounded w-3/4"></div>
              <div className="h-3 bg-admin-dark-700 rounded w-1/2"></div>
            </div>
            <div className="flex gap-2">
              <div className="h-6 w-16 bg-admin-dark-700 rounded"></div>
              <div className="h-6 w-20 bg-admin-dark-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="admin-card p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-admin-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="w-8 h-8 text-admin-slate-500" />
          </div>
          <h3 className="text-heading-4 text-admin-slate-400 mb-2">
            No activities yet
          </h3>
          <p className="text-body-sm text-admin-slate-500 mb-6">
            Get started by creating your first activity for the platform.
          </p>
          {onCreateFirst && (
            <button onClick={onCreateFirst} className="admin-button-primary">
              Create Your First Activity
            </button>
          )}
        </div>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="admin-card p-12 text-center">
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 bg-admin-dark-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-admin-slate-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-heading-4 text-admin-slate-400 mb-2">No activities found</h3>
          <p className="text-body-sm text-admin-slate-500">
            Try adjusting your search or filters to find what you&apos;re looking for.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-admin-slate-300/10">
              <SortableHeader
                column="title"
                label="Title"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th className="px-6 py-4 text-left text-body-sm font-medium text-admin-slate-500 uppercase tracking-wider">
                Category
              </th>
              <SortableHeader
                column="status"
                label="Status"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <SortableHeader
                column="playType"
                label="Play Type"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <SortableHeader
                column="prepNeeded"
                label="Prep Needed"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <SortableHeader
                column="duration"
                label="Duration"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={onSort}
              />
              <th className="text-left px-6 py-4 text-body-sm font-medium text-admin-slate-500 uppercase tracking-wider">
                Labels
              </th>
              <th className="text-right px-6 py-4 text-body-sm font-medium text-admin-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-admin-slate-300/10">
            {activities.map((activity, index) => (
              <motion.tr
                key={activity.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="hover:bg-admin-dark-700/50 transition-colors"
              >
                <td className="px-6 py-5">
                  <div>
                    <button
                      onClick={() => handleActivityClick(activity.id)}
                      className="text-body font-medium text-admin-slate-400 hover:text-admin-accent-blue transition-colors text-left"
                    >
                      {activity.title}
                    </button>
                    <div className="text-body-sm text-admin-slate-600 line-clamp-1">
                      {activity.description}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-5">
                  {activity.category ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-500/10 text-teal-400 border border-teal-500/20">
                      {activity.category.name}
                    </span>
                  ) : (
                    <span className="text-body-sm text-admin-slate-600">—</span>
                  )}
                </td>
                <td className="px-6 py-5">
                  <StatusBadge status={activity.status} />
                </td>
                <td className="px-6 py-5 text-body text-admin-slate-400">
                  {activity.playType}
                </td>
                <td className="px-6 py-5 text-body text-admin-slate-400">
                  {activity.prepNeeded}
                </td>
                <td className="px-6 py-5 text-body text-admin-slate-400">
                  {activity.duration} min
                </td>
                {/* Labels: AI Generated · Tags · Time Periods · Age Groups */}
                <td className="px-6 py-5">
                  <div className="flex flex-wrap gap-1">
                    {activity.isAiGenerated && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/20">
                        AI Generated
                      </span>
                    )}
                    {activity.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-admin-accent-purple/10 text-admin-accent-purple border border-admin-accent-purple/20"
                      >
                        {tag.label}
                      </span>
                    ))}
                    {activity.transitions.map((transition) => (
                      <span
                        key={transition.id}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-500 border border-amber-500/20"
                      >
                        {transition.label}
                      </span>
                    ))}
                    {activity.ageRanges.map((ar) => (
                      <span
                        key={`${ar.fromAge}-${ar.toAge}`}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20"
                      >
                        {ar.fromAge}–{ar.toAge} yrs
                      </span>
                    ))}
                    {!activity.isAiGenerated &&
                      activity.tags.length === 0 &&
                      activity.transitions.length === 0 &&
                      activity.ageRanges.length === 0 && (
                        <span className="text-body-sm text-admin-slate-600">—</span>
                      )}
                  </div>
                </td>
                <td className="px-6 py-5">
                  <div className="flex items-center justify-end gap-2">
                    <Link to={`/admin/activities/${activity.id}`}>
                      <Button variant="ghost" size="sm" title="View Details">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </Link>
                    <Link to={`/admin/activities/${activity.id}/edit`}>
                      <Button variant="ghost" size="sm" title="Edit Activity">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: string }) {
  const colors = {
    draft: 'bg-admin-slate-600/20 text-admin-slate-400',
    published: 'bg-green-500/20 text-green-400',
    archived: 'bg-amber-500/20 text-amber-400',
    Draft: 'bg-admin-slate-600/20 text-admin-slate-400',
    Published: 'bg-green-500/20 text-green-400',
    Archived: 'bg-amber-500/20 text-amber-400',
  };

  const color = colors[status as keyof typeof colors] || colors.draft;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

/**
 * Sortable column header component
 */
interface SortableHeaderProps {
  column: ActivityFilters['sortBy'];
  label: string;
  sortBy: ActivityFilters['sortBy'];
  sortOrder: ActivityFilters['sortOrder'];
  onSort: (column: ActivityFilters['sortBy']) => void;
}

function SortableHeader({ column, label, sortBy, sortOrder, onSort }: SortableHeaderProps) {
  const isActive = sortBy === column;
  
  return (
    <th className="text-left px-6 py-4 text-body-sm font-medium text-admin-slate-500 uppercase tracking-wider">
      <button
        onClick={() => onSort(column)}
        className="flex items-center gap-2 hover:text-admin-accent-blue transition-colors group"
      >
        <span>{label}</span>
        <div className="flex flex-col">
          <ChevronUp
            className={`w-3 h-3 -mb-1 transition-colors ${
              isActive && sortOrder === 'asc'
                ? 'text-admin-accent-blue'
                : 'text-admin-slate-600 group-hover:text-admin-slate-400'
            }`}
          />
          <ChevronDown
            className={`w-3 h-3 -mt-1 transition-colors ${
              isActive && sortOrder === 'desc'
                ? 'text-admin-accent-blue'
                : 'text-admin-slate-600 group-hover:text-admin-slate-400'
            }`}
          />
        </div>
      </button>
    </th>
  );
}
